import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Snackbar,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelector } from "react-redux";
import { TEAMS_CLIENT_ID, ENVIRONMENT_NAME } from "../../config";

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`teams-tabpanel-${index}`}
            aria-labelledby={`teams-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `teams-tab-${index}`,
        'aria-controls': `teams-tabpanel-${index}`,
    };
}

function TeamsIntegrationSettings(props) {
    const { data: propsData } = props;
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [subTabValue, setSubTabValue] = useState(0);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleSubTabChange = (event, newValue) => {
        setSubTabValue(newValue);
    };

    const handleGrantAdminConsent = () => {
        if (!TEAMS_CLIENT_ID) {
            setSnackbarMessage("Teams Client ID is not configured for this environment. Please contact support.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const redirectUri = window.location.origin + '/admin-consent.html';
        const tenantId = 'common';

        const consentUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent?` +
            `client_id=${TEAMS_CLIENT_ID}` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&state=${Date.now()}`;

        window.open(consentUrl, '_blank', 'width=600,height=700,scrollbars=yes');
    };

    const handleDownloadApp = () => {
        // Get the correct file based on environment
        const fileName = ENVIRONMENT_NAME === 'prod'
            ? 'Boxphish-Production.zip'
            : 'Boxphish-Dev.zip';

        const link = document.createElement('a');
        link.href = `/assets/AppFile/${fileName}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Define tabs
    const allTabs = [
        {
            label: "Settings",
            component: (
                <Grid container spacing={2}>
                    {/* Download App Card */}
                    <Grid item xs={12} md={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">
                                    Download Teams App
                                </Typography>
                            </Box>
                            <Box mb={3} mt={1}>
                                <Typography variant="body2" color="text.secondary">
                                    Download the Boxphish Teams App package to install in your Microsoft Teams environment. This app enables users to view and complete their training courses directly within Teams.
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                                <Button
                                    onClick={handleDownloadApp}
                                    variant="contained"
                                    color="primary"
                                    sx={{ width: '80%' }}
                                    startIcon={<DownloadIcon />}
                                >
                                    Download App Package
                                </Button>
                            </Box>
                        </MainCard>
                    </Grid>

                    {/* Grant Admin Consent Card */}
                    <Grid item xs={12} md={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">
                                    Admin Consent
                                </Typography>
                            </Box>
                            <Box mb={2} mt={1}>
                                <Typography variant="body2" color="text.secondary">
                                    Grant admin consent for the Teams App to enable notifications for your users.
                                </Typography>
                            </Box>

                            <Box sx={{
                                bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                                borderRadius: 2,
                                p: 2,
                                mb: 2
                            }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Permissions requested:
                                </Typography>
                                <List dense disablePadding>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Send notifications to users"
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Access basic user profile information"
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Read organisation information"
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                </List>
                            </Box>

                            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                                <Button
                                    onClick={handleGrantAdminConsent}
                                    variant="contained"
                                    color="warning"
                                    sx={{ width: '80%' }}
                                    startIcon={<CheckCircleIcon />}
                                >
                                    Grant Admin Consent
                                </Button>
                            </Box>

                            <Alert severity="warning" sx={{ textAlign: 'left', mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Note:</strong> You must be a Microsoft 365 Global Administrator to grant consent.
                                </Typography>
                            </Alert>
                        </MainCard>
                    </Grid>
                </Grid>
            )
        },
        {
            label: "Install",
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title="install"
                    src="https://boxphishsupport.helpdocs.io/article/8xp7851lyb-teams-integration-installation"
                />
            )
        }
    ];

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="teams integration tabs">
                        {allTabs.map((tab, index) => (
                            <Tab key={index} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                </Box>
                {allTabs.map((tab, index) => (
                    <TabPanel key={index} value={subTabValue} index={index}>
                        {tab.component}
                    </TabPanel>
                ))}
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                       sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

TeamsIntegrationSettings.propTypes = {
    data: PropTypes.object
};

export default TeamsIntegrationSettings;
